import React, { useState, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { uploadLightReachDocument } from "../../dtos/lightreach";
import { CircularProgress } from "@material-ui/core";

const ACCEPTABLE_DOCS = [
  "Drivers license or other state issued ID card",
  "Passport or passport card",
  "Resident permit ID or green card",
];

/**
 * Modal for uploading proof of identity to LightReach. Uses uploadDocument
 * with type "identityVerification".
 */
export default function IdentityVerificationModal({ show, onHide, acctId, onUploadSuccess }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    setFiles([]);
    setError(null);
    onHide();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files;
    if (selected?.length) {
      setFiles(Array.from(selected));
    }
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setError(null);
    setUploading(true);
    try {
      await uploadLightReachDocument(acctId, "identityVerification", files);
      if (typeof onUploadSuccess === "function") {
        onUploadSuccess();
      }
      handleClose();
    } catch (err) {
      setError(err?.message || "Upload failed. Try again or contact Sales Support.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Upload Proof of Identity</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-info mb-3 mb-0" role="alert">
          Verification is faster if the customer submits proof of identity via their app account.
        </div>
        <p className="mb-2 mt-3">Acceptable docs:</p>
        <ul className="mb-3">
          {ACCEPTABLE_DOCS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="mb-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose Files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="d-none"
            onChange={handleFileChange}
          />
          {files.length > 0 && (
            <div className="small mt-2 text-muted">
              {files.length} file(s) selected
            </div>
          )}
        </div>
        {error && (
          <div className="text-danger small mt-2">{error}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={uploading || files.length === 0 || !acctId}
        >
          {uploading ? (
            <>
              <CircularProgress size={16} color="inherit" className="me-1" />
              Uploading…
            </>
          ) : (
            "Upload"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
